import _ from "lodash";
import { String } from "typescript-string-operations";
import enResources from "../localization/resource_en.json";
import esResources from "../localization/resource_es.json";
import { appStore } from "../modules/store";

export class LocalizationService {
  locale: string;
  resources: any;
  private unsubscribe: (() => void) | null = null;

  constructor() {
    // Initialize with default locale
    this.locale = "en";
    this.resources = enResources;

    // Subscribe to store changes to update locale when account loads
    this.unsubscribe = appStore.subscribe(() => {
      this.updateLocaleFromStore();
    });

    // Try to set initial locale from store
    this.updateLocaleFromStore();
  }

  private updateLocaleFromStore() {
    const state = appStore.getState();

    // Only update if account is loaded
    if (!state.account?.isLoading) {
      // Get language from store, default to "en" if not set
      const newLocale = state.account?.language?.toLowerCase().trim() || "en";

      // Only update if locale changed
      if (newLocale !== this.locale) {
        this.locale = newLocale;
        this.updateResources();
      }
    }
  }

  private updateResources() {
    // Normalize locale to lowercase
    const normalizedLocale = this.locale?.toLowerCase().trim() || "en";

    // Try to load the requested language, fallback to English if not found
    if (normalizedLocale === "es") {
      this.resources = esResources || enResources;
    } else {
      // Default to English for any other language or if language file doesn't exist
      this.resources = enResources;
    }
  }

  // Cleanup method if needed
  destroy() {
    if (this.unsubscribe) {
      this.unsubscribe();
      this.unsubscribe = null;
    }
  }

  getLocalizationStrings() {
    return this.resources;
  }

  // Examples:
  // localizationService.getLocalizedString('errors.failed')
  // localizationService.getLocalizedString('errors.failedPattern', 'domainObjects.extractor');
  // localizationService.getLocalizedString('errors.failedPattern', '2013-01-02');
  getLocalizedString(jsonPath: string, ...argumentJsonPaths: string[]): string {
    return this.getLocalizedStringAndLog(jsonPath, argumentJsonPaths, true);
    // return this.getLocalizedStringAndLog("test." + jsonPath, argumentJsonPaths, true);
  }

  formatValue(value: any): any {
    if (typeof value === "boolean") {
      return value === true
        ? localizationService.getLocalizedString("constants.yes")
        : localizationService.getLocalizedString("constants.no");
    }
    return value;
  }

  private getLocalizedStringAndLog(
    jsonPath: string,
    argumentJsonPaths: string[],
    logAsWarning: boolean
  ): string {
    if (!jsonPath) {
      return "";
    }

    // Try to get the localized text from current resources
    let localizedText = _.get(this.resources, jsonPath);

    // If not found in current language, fallback to English
    if (!localizedText && this.locale !== "en") {
      localizedText = _.get(enResources, jsonPath);
      if (logAsWarning && localizedText) {
        console.warn(
          `Translation for '${jsonPath}' not found in '${this.locale}', using English fallback.`
        );
      }
    }

    if (localizedText) {
      if (argumentJsonPaths.length > 0) {
        const localizedArguments = argumentJsonPaths.map((x) => {
          return this.getLocalizedStringAndLog(x, [], false);
        });
        return String.format(localizedText, ...localizedArguments);
      }
      return localizedText;
    }

    if (logAsWarning) {
      console.warn(
        `Translation for '${jsonPath}' is missing in both '${this.locale}' and English.`
      );
    }
    return jsonPath;
  }
}

export const localizationService = new LocalizationService();
